import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedList, FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import {
  RevealPropertyCurrency,
  type Rarity,
} from '@sorare/core/src/__generated__/globalTypes';
import { Shards } from '@sorare/core/src/atoms/icons/Shards';
import { RadioGroup } from '@sorare/core/src/atoms/inputs/RadioGroup';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyS, HeadlineL, LabelL } from '@sorare/core/src/atoms/typography';
import { ConfirmDialog } from '@sorare/core/src/components/form/ConfirmDialog';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport/index.ts';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { getRarityColor } from '@sorare/core/src/lib/colors';
import { glossary } from '@sorare/core/src/lib/glossary';

import { getClueAssets } from '#src/lib/crafting.ts';

import type { CluesPickerConfirmDialog_revealCost } from './__generated__/index.graphql.ts';

const Root = styled(Vertical).attrs({
  gap: 2,
  center: true,
})`
  padding: 0 var(--double-unit);
  align-items: center;
`;

type Props = {
  revealCost: Nullable<CluesPickerConfirmDialog_revealCost>;
  rarity: Rarity;
  onClose: () => void;
  onConfirm: (spending?: RevealPropertyCurrency) => void;
};

export const CluesPickerConfirmDialog = ({
  revealCost,
  rarity,
  onClose,
  onConfirm,
}: Props) => {
  const { sport } = useSportContext();
  const cluesAssets = getClueAssets(sport);
  const { currentUser } = useCurrentUserContext();
  const {
    flags: { useRevealPropertyCurrency = false },
  } = useFeatureFlags();
  const craftClue = currentUser?.craftClues?.find(
    clue => clue.currency === revealCost?.currency
  );
  const hasEnoughClues = revealCost
    ? (craftClue?.amount ?? 0) >= revealCost?.amountInCurrency
    : false;

  const [spending, setSpending] = useState<RevealPropertyCurrency | undefined>(
    () => {
      if (!useRevealPropertyCurrency) {
        return undefined;
      }
      return hasEnoughClues
        ? RevealPropertyCurrency.IN_GAME_CURRENCY
        : RevealPropertyCurrency.CARD_SHARDS;
    }
  );

  const cardShardChest = currentUser?.cardShardsChests?.find(
    chest => chest.rarity === rarity
  );

  const hasEnoughShards = revealCost
    ? (cardShardChest?.cardShardsCount ?? 0) >= revealCost?.amountInShards
    : false;

  return (
    <ConfirmDialog
      open={!!revealCost}
      fullScreen={false}
      onClose={onClose}
      onConfirm={() => onConfirm(spending)}
      title={
        !!revealCost && (
          <img
            src={cluesAssets[revealCost.propertyName].iconUrl}
            alt=""
            style={{
              width: 'var(--quadruple-unit)',
              height: 'var(--quadruple-unit)',
            }}
          />
        )
      }
      message={
        !!revealCost && (
          <Root>
            <Vertical gap={0.5} center>
              <HeadlineL>
                <FormattedMessage
                  {...cluesAssets[revealCost.propertyName].label}
                />
              </HeadlineL>

              <LabelL color="var(--c-nd-600)">
                <FormattedMessage
                  {...cluesAssets[revealCost.propertyName].description}
                />
              </LabelL>

              <BodyS bold>
                <Horizontal>
                  <FormattedMessage {...glossary.cost} />
                  <FormattedList
                    type="disjunction"
                    value={[
                      <Horizontal gap={0.5} key="currency">
                        <img
                          style={{
                            width: 'var(--double-unit)',
                            height: 'var(--double-unit)',
                          }}
                          src={cluesAssets[revealCost.propertyName].iconUrl}
                          alt=""
                        />
                        <span>{revealCost.amountInCurrency}</span>
                      </Horizontal>,
                      <Horizontal gap={0.5} key="shards">
                        <Shards color={getRarityColor(rarity)} />
                        {revealCost?.amountInShards}
                      </Horizontal>,
                    ]}
                  />
                </Horizontal>
              </BodyS>
            </Vertical>

            {useRevealPropertyCurrency ? (
              <>
                <LabelL bold>
                  <FormattedMessage defaultMessage="What do you want to spend?" />
                </LabelL>

                <Vertical style={{ alignSelf: 'stretch' }} gap={1}>
                  <RadioGroup
                    rounded
                    border
                    reverse
                    withSpacing
                    initiallySelectedValue={spending}
                    name="spending"
                    onChange={setSpending}
                    options={[
                      {
                        disabled: !hasEnoughClues,
                        label: (
                          <Horizontal gap={0.5}>
                            <img
                              style={{
                                width: 'var(--double-unit)',
                                height: 'var(--double-unit)',
                              }}
                              src={cluesAssets[revealCost.propertyName].iconUrl}
                              alt=""
                            />
                            <LabelL>
                              <FormattedMessage
                                defaultMessage="{cost, plural, one {1 Clue} other {# Clues}} <small>({available} available)</small>"
                                values={{
                                  cost: revealCost.amountInCurrency,
                                  available: craftClue?.amount ?? 10,
                                  small: (chunks: React.ReactNode) => (
                                    <small
                                      style={{
                                        color: hasEnoughClues
                                          ? 'var(--c-nd-500)'
                                          : 'var(--c-red-400)',
                                      }}
                                    >
                                      {chunks}
                                    </small>
                                  ),
                                }}
                              />
                            </LabelL>
                          </Horizontal>
                        ),
                        value: RevealPropertyCurrency.IN_GAME_CURRENCY,
                      },
                      {
                        disabled: !hasEnoughShards,
                        label: (
                          <Horizontal gap={0.5}>
                            <Shards color={getRarityColor(rarity)} />
                            <LabelL>
                              <FormattedMessage
                                defaultMessage="{cost} Essence <small>({available} available)</small>"
                                values={{
                                  cost: revealCost.amountInShards,
                                  available:
                                    cardShardChest?.cardShardsCount ?? 0,
                                  small: chunks => (
                                    <small
                                      style={{
                                        color: hasEnoughShards
                                          ? 'var(--c-nd-500)'
                                          : 'var(--c-red-400)',
                                      }}
                                    >
                                      {chunks}
                                    </small>
                                  ),
                                }}
                              />
                            </LabelL>
                          </Horizontal>
                        ),
                        value: RevealPropertyCurrency.CARD_SHARDS,
                      },
                    ]}
                  />
                </Vertical>
              </>
            ) : undefined}
          </Root>
        )
      }
      ctaProps={{
        disabled: !hasEnoughClues && !hasEnoughShards,
      }}
      cancelCta={<FormattedMessage {...glossary.cancel} />}
    />
  );
};

CluesPickerConfirmDialog.fragments = {
  revealCost: gql`
    fragment CluesPickerConfirmDialog_revealCost on RevealCost {
      propertyName
      amountInCurrency
      amountInShards
      currency
    }
  ` as TypedDocumentNode<CluesPickerConfirmDialog_revealCost>,
};
